import React, { Suspense, useCallback, useState } from 'react';
import { pick } from 'ramda';
import { useCookieSettingsSelector } from '@core/js/store/hooks';
import { useComponentTranslations } from '@core/js/hooks/component/useComponentTranslations';
import { Loader } from '@core/js/components/common/Loader';
import { ManagedPopup } from '@core/js/components/common/Popup';
import { withComponent } from '@core/js/components/hocs/withComponent';
import { CookieButtons, ContentRenderer, CookieTabSwitcher } from './components';

import { useShowingMode, useSubmitCookies, useHandleAccept, useHandleClose } from './hooks';
import { ACTIVE_TAB_POPUP, SHOWING_MODE } from './constants';

import style from './CookiesRegulationPopup.scss';

export const CookiesRegulationPopupBare: React.FC<{ id: string }> = ({ id }) => {
    const translate = useComponentTranslations();
    const showingMode = useShowingMode();
    const handleClose = useHandleClose(id);
    const handleAccept = useHandleAccept(id);
    const handleSubmitCookies = useSubmitCookies(id);

    const { groupsData } = useCookieSettingsSelector(pick(['groupsData']));
    const [activeTab, setActiveTab] = useState<ACTIVE_TAB_POPUP>(ACTIVE_TAB_POPUP.MESSAGE);
    const [data, setData] = useState({ ...groupsData });

    const handleSubmitCookieSettings = useCallback((event) => {
        event.preventDefault();

        handleSubmitCookies(data);
    }, [data, handleSubmitCookies]);

    return (
        <ManagedPopup
            className={style.popup}
            classNames={{
                inner: style.inner,
                close: style.closeIcon
            }}
            layerClassNames={{ layout: style.layout }}
            isModal={showingMode !== SHOWING_MODE.COMMON}
            onClose={handleClose}
        >
            <div className={style.cookieContainer}>
                <div className={style.cookieContainerHeader}>
                    <CookieTabSwitcher
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                </div>
                <div className={style.cookieContainerContent}>
                    <Suspense fallback={<Loader />}>
                        <ContentRenderer
                            activeTab={activeTab}
                            data={data}
                            setData={setData}
                        />
                    </Suspense>
                </div>
                <div className={style.cookieContainerFooter}>
                    <CookieButtons
                        activeTab={activeTab}
                        translate={translate}
                        handleSubmitCookieSettings={handleSubmitCookieSettings}
                        handleAccept={handleAccept}
                        setActiveTab={setActiveTab}
                    />
                </div>
            </div>
        </ManagedPopup>
    );
};

export const CookiesRegulationPopup = withComponent({
    tkPrefix: 'playtech.mobile.system.cookie-regulation-popup.'
})(CookiesRegulationPopupBare);
